/**
 * Infinite Scroll for Shopify Collections
 * Based on Shopify Dawn theme official implementation
 * Rebuilt from scratch for maximum browser compatibility
 *
 * Usage: Wrap your product grid and pagination with the custom element:
 * <infinite-scroll-element
 *   data-products-container="#facets-results"
 *   data-pagination-selector=".pagination-wrapper"
 *   data-next-page-selector="a[rel='next']">
 * </infinite-scroll-element>
 */

class InfiniteScrollElement extends HTMLElement {
  constructor() {
    super();

    // Get configuration from data attributes
    this.productsContainerSelector = this.getAttribute('data-products-container') || '#facets-results';
    this.paginationSelector = this.getAttribute('data-pagination-selector') || '.pagination-wrapper';
    this.nextPageSelector = this.getAttribute('data-next-page-selector') || 'a[rel="next"]';
    this.sectionId = this.getAttribute('data-section-id');

    // Find DOM elements
    this.productsContainer = this.querySelector(this.productsContainerSelector);
    this.paginationWrapper = this.querySelector(this.paginationSelector);
    this.loadingIndicator = this.querySelector('.loading-overlay-wrapper, [data-infinite-loading-indicator]');

    // State
    this.isLoading = false;
    this.observer = null;

    // Validate required elements
    if (!this.productsContainer) {
      console.error('[InfiniteScroll] Products container not found:', this.productsContainerSelector);
      return;
    }

    if (!this.paginationWrapper) {
      console.error('[InfiniteScroll] Pagination wrapper not found:', this.paginationSelector);
      return;
    }

    // Initialize
    this.init();
  }

  init() {
    try {
      // Create and setup intersection observer
      this.observer = this.createObserver();

      // Start observing the pagination wrapper
      // CRITICAL: Observe the pagination wrapper itself, not a sentinel element
      // This works more reliably across browsers, especially Chrome
      this.observer.observe(this.paginationWrapper);

      // Hide pagination visually but keep it in the DOM for observer
      this.paginationWrapper.style.opacity = '0';
      this.paginationWrapper.style.pointerEvents = 'none';
      this.paginationWrapper.style.position = 'relative';

      console.log('[InfiniteScroll] Initialized successfully');
    } catch (error) {
      console.error('[InfiniteScroll] Initialization failed:', error);
    }
  }

  createObserver() {
    // Use a promise-based callback to prevent race conditions
    let callbackIsRunning = false;

    return new IntersectionObserver(
      (entries) => {
        // CRITICAL: Check isIntersecting AND ensure callback isn't already running
        // This prevents duplicate requests in Chrome
        if (entries[0].isIntersecting && !callbackIsRunning && !this.isLoading) {
          callbackIsRunning = true;

          this.loadNextPage()
            .then(() => {
              callbackIsRunning = false;
            })
            .catch((error) => {
              console.error('[InfiniteScroll] Load failed:', error);
              callbackIsRunning = false;
            });
        }
      },
      {
        // CRITICAL for Chrome: Use rootMargin to trigger BEFORE element reaches viewport
        // Positive top margin triggers earlier, creating smooth preloading
        rootMargin: '200px 0px 0px 0px',
        // CRITICAL: threshold 0 is more reliable than threshold 1.0 in Chrome
        // It triggers as soon as ANY part of the element is visible
        threshold: 0
      }
    );
  }

  async loadNextPage() {
    // Prevent duplicate requests
    if (this.isLoading) {
      return Promise.resolve();
    }

    // Find the next page link
    const nextPageLink = this.paginationWrapper.querySelector(this.nextPageSelector);

    if (!nextPageLink) {
      // No more pages - disconnect observer
      console.log('[InfiniteScroll] No more pages, disconnecting observer');
      if (this.observer) {
        this.observer.disconnect();
      }
      // Hide pagination completely
      if (this.paginationWrapper) {
        this.paginationWrapper.style.display = 'none';
      }
      return Promise.resolve();
    }

    const nextPageUrl = nextPageLink.getAttribute('href');

    if (!nextPageUrl) {
      console.error('[InfiniteScroll] Next page URL not found');
      return Promise.reject(new Error('Next page URL not found'));
    }

    console.log('[InfiniteScroll] Loading next page:', nextPageUrl);
    this.isLoading = true;

    // Show loading indicator
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.remove('hidden');
      this.loadingIndicator.style.display = 'block';
    }

    try {
      // Fetch the next page
      const response = await fetch(nextPageUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const htmlText = await response.text();

      // Parse and append the new content
      await this.handleNextPage(htmlText);

      console.log('[InfiniteScroll] Page loaded successfully');

    } catch (error) {
      console.error('[InfiniteScroll] Fetch error:', error);
      throw error;
    } finally {
      this.isLoading = false;

      // Hide loading indicator
      if (this.loadingIndicator) {
        this.loadingIndicator.classList.add('hidden');
        this.loadingIndicator.style.display = 'none';
      }
    }
  }

  async handleNextPage(htmlContent) {
    // Parse the HTML response
    const parser = new DOMParser();
    const nextPageDoc = parser.parseFromString(htmlContent, 'text/html');

    // Find the products in the response
    const nextPageProducts = nextPageDoc.querySelector(this.productsContainerSelector);

    if (!nextPageProducts || !nextPageProducts.innerHTML.trim()) {
      console.warn('[InfiniteScroll] No products found in next page');
      // Disconnect observer - no more content
      if (this.observer) {
        this.observer.disconnect();
      }
      return;
    }

    // CRITICAL: Use insertAdjacentHTML instead of appendChild for better performance
    // This is the Shopify Dawn approach and works better in Chrome
    this.productsContainer.insertAdjacentHTML('beforeend', nextPageProducts.innerHTML);

    console.log('[InfiniteScroll] Appended products from next page');

    // Update the pagination for the next iteration
    const nextPagePagination = nextPageDoc.querySelector(this.paginationSelector);

    if (!nextPagePagination) {
      console.warn('[InfiniteScroll] No pagination found in next page');
      // Disconnect observer - no more pages
      if (this.observer) {
        this.observer.disconnect();
      }
      // Hide pagination
      if (this.paginationWrapper) {
        this.paginationWrapper.style.display = 'none';
      }
      return;
    }

    // Replace pagination content
    this.paginationWrapper.innerHTML = nextPagePagination.innerHTML;

    // Dispatch custom events for other scripts (image loading, etc.)
    this.dispatchEvents();
  }

  dispatchEvents() {
    // Dispatch image loading event
    document.body.dispatchEvent(
      new CustomEvent('switch:load:images', { bubbles: true })
    );

    // Dispatch collection updated event
    document.dispatchEvent(
      new CustomEvent('collection:updated', {
        detail: {
          sectionId: this.sectionId,
          fromInfiniteScroll: true
        },
        bubbles: true
      })
    );
  }

  // Public method to disconnect observer (useful for filters)
  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  // Public method to reconnect observer (useful for filters)
  reconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.init();
  }
}

// Define the custom element
// CRITICAL: Use CustomElementRegistry to avoid duplicate definitions
if (!customElements.get('infinite-scroll-element')) {
  customElements.define('infinite-scroll-element', InfiniteScrollElement);
  console.log('[InfiniteScroll] Custom element registered');
}

// Handle filter/sort updates
// When filters change, the infinite scroll element will be replaced by Shopify's section rendering
// The new element will auto-initialize via the constructor
document.addEventListener('collection:updated', (event) => {
  // Skip if event is from infinite scroll itself
  if (event.detail && event.detail.fromInfiniteScroll) {
    return;
  }

  console.log('[InfiniteScroll] Collection updated, elements will reinitialize automatically');
});
